import {FC, useReducer} from "react";
import styled from "styled-components";
import {useTranslation} from "react-i18next";
import {Visibility, VisibilityOff} from "@mui/icons-material";
import {FormControl, IconButton, InputAdornment, TextField, TextFieldProps} from "@mui/material";
import {POST_CODE_PATTERN, ZIP_CODE_PATTERN} from "modules/constants";

export const Input = styled(TextField)`
	width: 100%;
	background: rgba(255, 255, 255, 0.09);
	border-radius: 4px;

	&& label {
		&.Mui-disabled {
			opacity: 0.5;
		}

		color: ${({theme}) => theme.background.active};
	}

	& .MuiInputBase-root,
	.MuiAutocomplete-inputRoot {
		color: ${({theme}) => theme.color.tertiary};
		-webkit-text-fill-color: ${({theme}) => theme.color.tertiary};

		&:after {
			border-color: ${({theme}) => theme.background.secondary};
		}

		&.Mui-error:after {
			border-color: ${({theme}) => theme.color.error};
		}

		&.Mui-disabled input,
		&.Mui-disabled div {
			color: rgba(255, 255, 255, 0.6);
			-webkit-text-fill-color: rgba(255, 255, 255, 0.6);
		}
	}

	// input Adornment color
	& .MuiIconButton-root path {
		fill: rgba(255, 255, 255, 0.56);
	}

	& .MuiFormHelperText-root {
		color: #a3a3a3;
	}

	// select arrow color
	& .MuiSvgIcon-root {
		color: rgba(255, 255, 255, 0.56);
	}

	& .MuiFormHelperText-root.Mui-error {
		color: ${({theme}) => theme.color.error};
	}
`;

Input.defaultProps = {
	SelectProps: {
		MenuProps: {
			PaperProps: {
				sx: {
					bgcolor: "#121212",
					color: "#ffffff",

					"& .MuiMenuItem-root.Mui-selected": {
						backgroundColor: "#FFFFFF14",
					},
					"& .MuiMenuItem-root:hover": {
						backgroundColor: "#FFFFFF14",
					},
					"& .MuiMenuItem-root.Mui-selected:hover": {
						backgroundColor: "#FFFFFF14",
					},
				},
			},
		},
	},
};

// Used as a wrapper for a form element, to update styles or add margin.
export const FormField = styled(FormControl)`
	&.MuiFormControl-root {
		width: 100%;
		margin-bottom: 16px;
	}
`;

export const FormError = styled.div`
	font-family: "Hind", sans-serif;
	color: ${({theme}) => theme.color.error};
`;

export const PasswordInput: FC<TextFieldProps> = (props) => {
	const [isShow, toggleIsShow] = useReducer((isOpen) => !isOpen, false);

	return (
		<Input
			variant="filled"
			type={isShow ? "text" : "password"}
			InputProps={{
				endAdornment: (
					<InputAdornment position="end">
						<IconButton onClick={toggleIsShow} edge="end">
							{isShow ? <Visibility /> : <VisibilityOff />}
						</IconButton>
					</InputAdornment>
				),
			}}
			{...props}
		/>
	);
};

const LocationInput = styled(Input)`
	input {
		text-transform: uppercase;

		::placeholder {
			text-transform: initial;
		}
	}
`;

type TLocationCodeInput = TextFieldProps & {
	country: string;
};

export const LocationCodeInput: FC<TLocationCodeInput> = (props) => {
	const {t} = useTranslation();

	if (props.country === "US") {
		return (
			<FormField>
				<LocationInput
					{...props}
					label={t("form.registration.zip_code_label")}
					placeholder={t("form.registration.zip_code_placeholder")}
					inputProps={{
						pattern: ZIP_CODE_PATTERN,
					}}
				/>
			</FormField>
		);
	}

	if (props.country === "CA") {
		return (
			<FormField>
				<LocationInput
					{...props}
					label={t("form.registration.post_code_label")}
					placeholder={t("form.registration.post_code_placeholder")}
					inputProps={{
						pattern: POST_CODE_PATTERN,
					}}
				/>
			</FormField>
		);
	}

	return null;
};
